/**
 * Settings → thresholds and the 12 flags — ADR-001 §8.1–§8.7, §11.8; operator decision 11 (founder and
 * associate director edit; every change audited). Guide §6: "These are the exact numbers in the prototype.
 * Keep them configurable."
 *
 * One `config_version` covers thresholds and flags. Every change bumps it, and it is the `If-Match` value for
 * both PATCHes, so two managers editing Settings never silently overwrite each other. A change never rewrites
 * a past health snapshot: each snapshot keeps the `config_version` it was computed with (ADR §7, §8.1).
 */
import { z } from 'zod';
import { Timestamp } from './common.js';
import { UserRef } from './users.js';
const int = (min, max) => z.number().int().min(min).max(max);
const pct = int(0, 100);
const penalty = int(0, 100);
// ---------------------------------------------------------------- thresholds, by group
/** ADR §8.1. Penalties are stored as positive points and subtracted; `bonus_positive` is added once. */
export const HealthThresholds = z
    .object({
    /** Band lines: healthy ≥ `band_healthy` (70), watch ≥ `band_watch` (45), at_risk below. */
    band_healthy: pct,
    band_watch: pct,
    /** The score never goes below this (5). */
    floor: pct,
    /** Weighted utilisation below `util_low` (40) → −`pen_util_low` (30). */
    util_low: pct,
    pen_util_low: penalty,
    /** Weighted utilisation from `util_low` up to below `util_watch` (60) → −`pen_util_watch` (15). */
    util_watch: pct,
    pen_util_watch: penalty,
    /** Weighted trend below `trend_fall` (−15) → −`pen_trend_fall` (20). */
    trend_fall: int(-100, 0),
    pen_trend_fall: penalty,
    /** Weighted trend from `trend_fall` to `trend_dip` (−5) → −`pen_trend_dip` (8). */
    trend_dip: int(-100, 0),
    pen_trend_dip: penalty,
    /** −`pen_overdue_each` (6) per overdue task, at most `overdue_cap` (20). */
    pen_overdue_each: penalty,
    overdue_cap: penalty,
    /** −`pen_escalation_each` (15) per open escalation. */
    pen_escalation_each: penalty,
    /** −`pen_request_breach_each` (8) per client request past its response time. */
    pen_request_breach_each: penalty,
    /** No contact for more than `quiet_days_hard` (14) days → −`pen_quiet_hard` (15). */
    quiet_days_hard: int(1, 90),
    pen_quiet_hard: penalty,
    /** No contact for more than `quiet_days_soft` (7) days, up to the hard line → −`pen_quiet_soft` (10). */
    quiet_days_soft: int(1, 90),
    pen_quiet_soft: penalty,
    /** Negative tone: the two worst inbound penalties in the last `tone_window_days` (14), at most `tone_cap` (30). */
    tone_window_days: int(1, 90),
    tone_cap: penalty,
    /** Inbound emails needing us for `needs_min_days` (2) days or more: −`pen_needs_each` (5) each, at most `needs_cap` (15). */
    needs_min_days: int(0, 30),
    pen_needs_each: penalty,
    needs_cap: penalty,
    /** Our outbound email unanswered `noreply_days` (7) days or more → −`pen_noreply` (5). */
    noreply_days: int(1, 60),
    pen_noreply: penalty,
    /** Any product with no usage data reported → −`pen_no_data` (5). */
    pen_no_data: penalty,
    /** A positive-tone inbound email in the window → +`bonus_positive` (5), once. */
    bonus_positive: penalty,
})
    .strict()
    .meta({ id: 'HealthThresholds' });
/**
 * ADR §8.3: least squares over `window_days`, slope × `damping`, projected `horizon_days`, capped at `util_cap`.
 * `min_r2` (0.3): a fit with r² below it is too noisy to draw — the forecast comes back `meaningful: false` and the
 * UI hides the line (ADR §6 usage_forecasts, §8.3 "r2 is stored so the UI can suppress a meaningless line").
 */
export const ForecastThresholds = z
    .object({
    damping: z.number().min(0.1).max(1),
    window_days: int(7, 60),
    horizon_days: int(7, 90),
    util_cap: int(100, 400),
    min_r2: z.number().min(0).max(1),
})
    .strict()
    .meta({ id: 'ForecastThresholds' });
/** ADR §8.7: a cell is amber when due today or within `due_soon_days` (3). */
export const EngagementThresholds = z.object({ due_soon_days: int(0, 14) }).strict().meta({ id: 'EngagementThresholds' });
/**
 * ADR §8.6; operator decision 9 (7.5 h). `on_time_by` is the IST time a day counts as filed on time.
 * `day_hours` is not a second copy: it is stored in the `working_day_hours` row (engine THRESHOLD_STORE), the one SQL
 * working_day_hours(), GET/PATCH /settings/working-day and /me config read, under the same config_version. A save
 * through either route moves the other's If-Match on.
 */
export const TimesheetThresholds = z
    .object({
    day_hours: z.number().min(1).max(12).multipleOf(0.25),
    on_time_by: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use a 24-hour time such as 18:30'),
})
    .strict()
    .meta({ id: 'TimesheetThresholds' });
/** ADR §8.4: auto-accept at `auto_threshold` (85, 60–99); the countdown is 60, 120 or 180 seconds. */
export const CaptureThresholds = z
    .object({
    auto_threshold: int(60, 99),
    countdown_seconds: z.union([z.literal(60), z.literal(120), z.literal(180)]),
})
    .strict()
    .meta({ id: 'CaptureThresholds' });
/** ADR §8.5: the tone labels and their health penalty points (positive is a bonus, so negative here). */
export const TONE_LABELS = ['leave', 'escalate', 'negative', 'positive', 'urgent', 'neutral'];
export const ToneThresholds = z
    .object({
    /** Tone detection on or off (the scope decision default is on). Off: no tone rows, tone penalty 0 for everyone. */
    enabled: z.boolean(),
    penalties: z
        .object({
        leave: penalty,
        escalate: penalty,
        negative: penalty,
        positive: int(-30, 0),
        urgent: penalty,
        neutral: penalty,
    })
        .strict(),
})
    .strict()
    .meta({ id: 'ToneThresholds' });
/** Settings already used by steps 2–3: the blocked-task flag (strictly more than N days) and the client response clock. */
export const WorkThresholds = z
    .object({
    block_flag_days: int(1, 30),
    client_response_hours: int(1, 168),
})
    .strict()
    .meta({ id: 'WorkThresholds' });
const ThresholdGroups = {
    health: HealthThresholds,
    forecast: ForecastThresholds,
    engagement: EngagementThresholds,
    timesheet: TimesheetThresholds,
    capture: CaptureThresholds,
    tone: ToneThresholds,
    work: WorkThresholds,
};
/**
 * The whole threshold set, with the cross-field rules. The API merges a PATCH into the current set and parses
 * the result with this schema, so a partial change that breaks an ordering is a `422` naming the field.
 */
export const Thresholds = z
    .object(ThresholdGroups)
    .strict()
    .superRefine((t, ctx) => {
    const h = t.health;
    const bad = (path, message) => ctx.addIssue({ code: 'custom', path, message });
    if (h.band_watch >= h.band_healthy)
        bad(['health', 'band_watch'], 'The watch line must be below the healthy line');
    if (h.floor >= h.band_watch)
        bad(['health', 'floor'], 'The lowest score must be below the watch line');
    if (h.util_low >= h.util_watch)
        bad(['health', 'util_low'], 'The low-utilisation line must be below the watch line');
    if (h.trend_fall >= h.trend_dip)
        bad(['health', 'trend_fall'], 'A fall must be a bigger drop than a dip');
    if (h.quiet_days_soft >= h.quiet_days_hard)
        bad(['health', 'quiet_days_soft'], 'The first quiet line must be shorter than the second');
    // The harder line never costs less than the softer one (S6-U-11). Equal is allowed. Named on the harder field.
    if (h.pen_util_low < h.pen_util_watch)
        bad(['health', 'pen_util_low'], 'Low utilisation must cost at least as much as the watch line');
    if (h.pen_trend_fall < h.pen_trend_dip)
        bad(['health', 'pen_trend_fall'], 'A fall must cost at least as much as a dip');
    if (h.pen_quiet_hard < h.pen_quiet_soft)
        bad(['health', 'pen_quiet_hard'], 'The second quiet line must cost at least as much as the first');
})
    .meta({ id: 'Thresholds' });
/** The prototype numbers (ADR §8.1–§8.7; decision 9) — what the settings seed policy writes, and what a reset returns to. */
export const DEFAULT_THRESHOLDS = {
    health: {
        band_healthy: 70, band_watch: 45, floor: 5,
        util_low: 40, pen_util_low: 30, util_watch: 60, pen_util_watch: 15,
        trend_fall: -15, pen_trend_fall: 20, trend_dip: -5, pen_trend_dip: 8,
        pen_overdue_each: 6, overdue_cap: 20, pen_escalation_each: 15, pen_request_breach_each: 8,
        quiet_days_hard: 14, pen_quiet_hard: 15, quiet_days_soft: 7, pen_quiet_soft: 10,
        tone_window_days: 14, tone_cap: 30,
        needs_min_days: 2, pen_needs_each: 5, needs_cap: 15,
        noreply_days: 7, pen_noreply: 5, pen_no_data: 5, bonus_positive: 5,
    },
    forecast: { damping: 0.6, window_days: 14, horizon_days: 30, util_cap: 160, min_r2: 0.3 },
    engagement: { due_soon_days: 3 },
    timesheet: { day_hours: 7.5, on_time_by: '18:30' },
    capture: { auto_threshold: 85, countdown_seconds: 120 },
    tone: { enabled: true, penalties: { leave: 20, escalate: 15, negative: 8, positive: -5, urgent: 0, neutral: 0 } },
    work: { block_flag_days: 3, client_response_hours: 24 },
};
/** PATCH /settings/thresholds — any subset of any group; at least one value. */
export const PatchThresholdsBody = z
    .object({
    health: HealthThresholds.partial().strict().optional(),
    forecast: ForecastThresholds.partial().strict().optional(),
    engagement: EngagementThresholds.partial().strict().optional(),
    timesheet: TimesheetThresholds.partial().strict().optional(),
    capture: CaptureThresholds.partial().strict().optional(),
    tone: z
        .object({ enabled: z.boolean().optional(), penalties: ToneThresholds.shape.penalties.partial().strict().optional() })
        .strict()
        .optional(),
    work: WorkThresholds.partial().strict().optional(),
})
    .strict()
    .refine((b) => Object.values(b).some((g) => g && Object.values(g).some((v) => v !== undefined && (typeof v !== 'object' || Object.keys(v).length > 0))), {
    message: 'Change at least one setting',
});
const ConfigMeta = {
    /** Bumped on every Settings change. Send it back as If-Match on the next PATCH. */
    config_version: z.number().int().positive(),
    updated_at: Timestamp.nullable(),
    /** Who made the last change; `null` for the seeded defaults. */
    updated_by: UserRef.nullable(),
};
export const ThresholdsResponse = z
    .object({ settings: Thresholds, defaults: Thresholds, ...ConfigMeta })
    .meta({ id: 'ThresholdsResponse' });
// ---------------------------------------------------------------- the 12 flags (ADR §8.2, guide §6.4)
export const FLAG_KEYS = [
    'util_below_low',
    'usage_below_baseline_2w',
    'no_usage_data_3d',
    'no_touchpoint_10d',
    'email_unanswered_3d',
    'client_task_overdue',
    'task_slipped_3x',
    'blocked_over_3d',
    'request_breach',
    'checklist_item_missed',
    'revenue_at_risk_top5',
    'held_item_over_4h',
];
export const FlagKey = z.enum(FLAG_KEYS).meta({ id: 'FlagKey' });
export const FlagTier = z.enum(['event', 'nightly', 'every_15_min', 'every_5_min']).meta({ id: 'FlagTier' });
/** Toggle wording (prototype `RULES`, the "Rules watching" panel) and tier for each flag (ADR §8.2). */
export const FLAG_INFO = {
    util_below_low: { label: 'Utilisation below 40% on a paid product', tier: 'nightly', manager_only: false },
    usage_below_baseline_2w: { label: "Usage 15% below the client's own baseline for 2 weeks", tier: 'nightly', manager_only: false },
    no_usage_data_3d: { label: 'No usage data for 3 days', tier: 'nightly', manager_only: false },
    no_touchpoint_10d: { label: 'No logged client touchpoint for 10 days', tier: 'nightly', manager_only: false },
    // Prototype RULES wording; the flag still covers both sides of a thread (decisions row 12) and each hit says which.
    email_unanswered_3d: { label: 'Email to client unanswered for 3 days', tier: 'event', manager_only: false },
    client_task_overdue: { label: 'Commitment past due', tier: 'event', manager_only: false },
    task_slipped_3x: { label: 'Task slipped to tomorrow 3 times', tier: 'event', manager_only: false },
    blocked_over_3d: { label: 'Blocked on someone for more than 3 days', tier: 'nightly', manager_only: false },
    request_breach: { label: 'Request breaching its response time', tier: 'every_15_min', manager_only: false },
    checklist_item_missed: { label: 'Checklist item missed past its due day', tier: 'nightly', manager_only: false },
    revenue_at_risk_top5: { label: 'Revenue at risk: top-5 client with falling usage', tier: 'nightly', manager_only: true },
    held_item_over_4h: { label: 'Held item waiting over 4 hours', tier: 'every_5_min', manager_only: false },
};
export const FlagState = z
    .object({
    key: FlagKey,
    label: z.string(),
    tier: FlagTier,
    /** Only managers ever see this flag's hits (revenue). */
    manager_only: z.boolean(),
    enabled: z.boolean(),
    /** Open `flag_hits` right now (0 when switched off: switching off clears them). */
    open_hits: z.number().int().nonnegative(),
})
    .meta({ id: 'FlagState' });
export const FlagsResponse = z.object({ flags: z.array(FlagState), ...ConfigMeta }).meta({ id: 'FlagsResponse' });
/** PATCH /settings/flags — `{ flag_key: boolean }` for any of the twelve; at least one. Off clears its open hits. */
export const PatchFlagsBody = z
    .partialRecord(FlagKey, z.boolean())
    .refine((b) => Object.keys(b).length > 0, { message: 'Switch at least one flag' });
//# sourceMappingURL=settings.js.map